import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Lock, ArrowRight, Loader2, Phone, Clock, ArrowLeft } from "lucide-react";
import { formatApiError } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phone sign-in stays parked until real SMS delivery (Twilio/MSG91) is connected.
const PHONE_LOGIN_ENABLED = false;

export default function AuthPage({ onLogin }) {
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setNotice("");

    const path =
      mode === "signup" ? "/auth/register" : mode === "forgot" ? "/auth/forgot-password" : "/auth/login";
    const body =
      mode === "signup"
        ? { email, password, name: name || undefined }
        : mode === "forgot"
          ? { email }
          : { email, password };

    try {
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data.detail, "Something went wrong. Please try again."));

      if (mode === "forgot") {
        setNotice(data.message || "If that email has an account, a reset link is on its way.");
        return;
      }
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const heading =
    mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Sign in";

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{ backgroundColor: "var(--mp-paper)" }}
      data-testid="auth-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-9">
          <Link
            to="/"
            className="text-3xl md:text-4xl font-medium"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            data-testid="auth-home-link"
          >
            Mutha Paras &amp; Co.
          </Link>
          <p
            className="text-xs uppercase tracking-[0.2em] font-semibold mt-2"
            style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
          >
            Buyer Account
          </p>
        </div>

        <div
          className="border rounded-sm p-7"
          style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-card)" }}
        >
          <h1
            className="text-2xl mb-1"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            data-testid="auth-heading"
          >
            {heading}
          </h1>
          <p
            className="text-sm mb-6"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif", lineHeight: 1.6 }}
          >
            {mode === "forgot"
              ? "We'll email you a link to choose a new password."
              : "Your past orders and details stay ready for next time. Browsing and ordering stay open without an account."}
          </p>

          {mode !== "forgot" && (
            <>
              <button
                onClick={googleSignIn}
                data-testid="google-signin-btn"
                className="flex items-center justify-center gap-2.5 w-full text-sm font-medium py-3 rounded-sm border transition-opacity hover:opacity-80"
                style={{
                  borderColor: "var(--mp-line)",
                  color: "var(--mp-text)",
                  backgroundColor: "var(--mp-surface)",
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                <GoogleMark />
                Continue with Google
              </button>

              <div className="flex items-center gap-3 my-5">
                <span className="h-px flex-1" style={{ backgroundColor: "var(--mp-line)" }} />
                <span
                  className="text-[11px] uppercase tracking-[0.14em]"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  or with email
                </span>
                <span className="h-px flex-1" style={{ backgroundColor: "var(--mp-line)" }} />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <LabelledInput
                icon={null}
                value={name}
                onChange={setName}
                placeholder="Shop or buyer name"
                testid="auth-name-input"
                autoComplete="name"
              />
            )}
            <LabelledInput
              icon={<Mail className="w-4 h-4 shrink-0" style={{ color: "var(--mp-muted)" }} />}
              value={email}
              onChange={setEmail}
              placeholder="Email address"
              type="email"
              required
              testid="auth-email-input"
              autoComplete="email"
            />
            {mode !== "forgot" && (
              <LabelledInput
                icon={<Lock className="w-4 h-4 shrink-0" style={{ color: "var(--mp-muted)" }} />}
                value={password}
                onChange={setPassword}
                placeholder={mode === "signup" ? "Password (at least 8 characters)" : "Password"}
                type="password"
                required
                testid="auth-password-input"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
              />
            )}

            {error && (
              <p
                className="text-xs"
                style={{ color: "#C2410C", fontFamily: "'Manrope', sans-serif" }}
                data-testid="auth-error"
              >
                {error}
              </p>
            )}
            {notice && (
              <p
                className="text-xs"
                style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
                data-testid="auth-notice"
              >
                {notice}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="flex items-center justify-center gap-2 w-full text-sm font-medium py-3.5 rounded-sm transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{
                backgroundColor: "var(--mp-copper-solid)",
                color: "#FFFFFF",
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
            </button>
          </form>

          <div className="mt-5 space-y-2 text-center">
            {mode === "signin" && (
              <>
                <TextButton onClick={() => { setMode("signup"); setError(""); setNotice(""); }} testid="auth-switch-signup">
                  New here? Create an account
                </TextButton>
                <TextButton onClick={() => { setMode("forgot"); setError(""); setNotice(""); }} testid="auth-switch-forgot">
                  Forgotten your password?
                </TextButton>
              </>
            )}
            {mode === "signup" && (
              <TextButton onClick={() => { setMode("signin"); setError(""); setNotice(""); }} testid="auth-switch-signin">
                Already have an account? Sign in
              </TextButton>
            )}
            {mode === "forgot" && (
              <TextButton onClick={() => { setMode("signin"); setError(""); setNotice(""); }} testid="auth-back-signin">
                <span className="inline-flex items-center gap-1.5">
                  <ArrowLeft className="w-3 h-3" /> Back to sign in
                </span>
              </TextButton>
            )}
          </div>
        </div>

        {!PHONE_LOGIN_ENABLED && (
          <div
            className="mt-4 flex items-start gap-2.5 border rounded-sm px-4 py-3"
            style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-surface)" }}
            data-testid="phone-login-coming-soon"
          >
            <Phone className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--mp-muted)" }} />
            <p
              className="text-xs"
              style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif", lineHeight: 1.6 }}
            >
              <span className="inline-flex items-center gap-1 font-semibold">
                <Clock className="w-3 h-3" /> Coming soon
              </span>{" "}
              — signing in with your mobile number and an OTP. Use Google or your email for now.
            </p>
          </div>
        )}

        <p className="mt-6 text-center text-xs" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
          <Link to="/catalogue/600-lining" data-testid="auth-browse-link" className="underline underline-offset-2">
            Browse the shade catalogue
          </Link>{" "}
          without signing in
        </p>
      </div>
    </div>
  );
}

const LabelledInput = ({ icon, value, onChange, placeholder, type = "text", required, testid, autoComplete }) => (
  <div
    className="flex items-center gap-2 px-3 py-2.5 rounded-sm border"
    style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-surface)" }}
  >
    {icon}
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      type={type}
      required={required}
      autoComplete={autoComplete}
      data-testid={testid}
      className="w-full min-w-0 text-sm bg-transparent outline-none"
      style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
    />
  </div>
);

const TextButton = ({ onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="block w-full text-xs transition-opacity hover:opacity-70"
    style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
  >
    {children}
  </button>
);

const GoogleMark = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
